/**
 * ContactOpportunitiesPage
 *
 * Admin view of a contact's "New" / Opportunities screen — mirrors the investor app
 * Opportunities tab (new funds available for EOI + the contact's active expressions of interest).
 *
 * Sections:
 *   1. Contact header + Edit link
 *   2. Active EOIs          (contact's investors with LEAD / OPPORTUNITY status)
 *   3. Available Funds      (all funds at EOI / APPLICATION_OPEN status — global)
 *   4. Notes
 *
 * Route: /contacts/:id/opportunities
 * Opened when admin clicks "New Funds" context chip in the Fides Chat panel.
 */

import AssignmentIcon from '@mui/icons-material/Assignment';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ActiveEoiSection from './ContactOpportunities/ActiveEoiSection';
import ContactNotesSection from './ContactOpportunities/ContactNotesSection';
import ContactOpportunitiesHeader from './ContactOpportunities/ContactOpportunitiesHeader';
import NewFundOpportunitiesSection from './ContactOpportunities/NewFundOpportunitiesSection';
import SectionCard from './ContactOpportunities/SectionCard';
import {
  getActiveEoiInvestors,
  partitionAvailableFunds,
} from './ContactOpportunities/contactOpportunitiesUtils';
import useContactOpportunitiesData from './ContactOpportunities/useContactOpportunitiesData';

export default function ContactOpportunitiesPage() {
  const { id: contactId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    contact,
    contactLoading,
    contactError,
    investments,
    investmentsLoading,
    availableFunds,
    fundsLoading,
  } = useContactOpportunitiesData(contactId);

  if (contactLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (contactError || !contact) {
    return (
      <Box p={3}>
        <Alert severity="error">{contactError ?? 'Contact not found'}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/contacts')}>
          Back to Contacts
        </Button>
      </Box>
    );
  }

  const activeEoiInvestors = getActiveEoiInvestors(investments);
  const { investedFundIds, newFunds } = partitionAvailableFunds(investments, availableFunds);

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
      <ContactOpportunitiesHeader
        contact={contact}
        onEditContact={() => navigate(`/contacts/${contactId}`)}
      />

      <ActiveEoiSection
        activeEoiInvestors={activeEoiInvestors}
        investmentsLoading={investmentsLoading}
        navigate={navigate}
      />

      <NewFundOpportunitiesSection
        availableFunds={availableFunds}
        fundsLoading={fundsLoading}
        investedFundIds={investedFundIds}
        newFunds={newFunds}
        navigate={navigate}
      />

      <SectionCard
        title="Notes"
        icon={<AssignmentIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
      >
        <ContactNotesSection contactExternalId={contactId!} />
      </SectionCard>
    </Box>
  );
}
